import {
    CryptoService,
    LockManager,
    ProviderBuilder,
    PulseRootConfigurationToken,
    PulseStorage,
    SharedConfigurations,
    StorageChangeOrigin,
    StorageSelector,
    StorageType,
    isLocalStorageSupported,
} from '@pulse/common';
import { PulseEnvironment } from '../globals';
import {
    ClientAttributesService,
    CookieChangeDetector,
    CookieParser,
    CookieStorage,
    DataLayerService,
    DateService,
    DebugControlsService,
    DeviceIdService,
    EventAdapterService,
    HashStore,
    HttpService,
    IndexedDBService,
    LoggerService,
    NativeLockManager,
    PageInfoService,
    PersistentStorage,
    PulseBroadcastChannel,
    Quote,
    RouteHelperService,
    StateService,
    TabManagerService,
    TabStorage,
    TrafficSourceService,
    TransientStorage,
    UTMTrackerService,
    UUIDService,
    UnsafeLockManager,
    WebToMobileService,
} from '../services';
import { DocumentVisibilityTracker } from '../services/visibility.tracker';
import { ActivityTrackerUtils } from '../utils';
import {
    CookieBroadcastChannelToken,
    LockManagerToken,
    PrefixedCookieStorageToken,
    StorageSelectorToken,
    UnPrefixedCookieStorageToken,
    UnPrefixedStorageToken,
    WindowToken,
} from './tokens';

export function sharedProviders(builder: ProviderBuilder) {
    return (
        builder
            .useFactory(SharedConfigurations, (config) => config, [PulseRootConfigurationToken])
            //
            // Locks
            ////////////////////////////
            .useClass(NativeLockManager, NativeLockManager, [PulseEnvironment, UUIDService])
            .useFactory(
                UnsafeLockManager,
                (
                    getStorage: StorageSelector,
                    dateService: DateService,
                    config: SharedConfigurations,
                    uuidService: UUIDService,
                    documentVisibilityTracker: DocumentVisibilityTracker,
                ) =>
                    new UnsafeLockManager(
                        getStorage(config.sessionStorageType),
                        dateService,
                        config,
                        uuidService,
                        documentVisibilityTracker,
                    ),
                [
                    StorageSelectorToken,
                    DateService,
                    SharedConfigurations,
                    UUIDService,
                    DocumentVisibilityTracker,
                ],
            )
            .useFactory(
                LockManagerToken,
                async (native: NativeLockManager, unsafe: UnsafeLockManager) =>
                    (await native.supported()) ? native : unsafe,
                [NativeLockManager, UnsafeLockManager],
            )
            .useClass(CryptoService, CryptoService, [])
            .useClass(CookieParser, CookieParser, [])
            .useFactory(
                LoggerService,
                (getStorage: StorageSelector, config: SharedConfigurations) =>
                    new LoggerService(getStorage(config.sessionStorageType), config),
                [StorageSelectorToken, SharedConfigurations],
            )
            .useClass(TransientStorage, TransientStorage, [])
            .useValue(WindowToken, window as any)
            .useClass(PulseEnvironment, PulseEnvironment, [WindowToken])
            .useClass(DateService, DateService, [PulseEnvironment])
            .useClass(HttpService, HttpService, [
                DocumentVisibilityTracker,
                PulseEnvironment,
                DateService,
                SharedConfigurations,
                DeviceIdService,
            ])
            .useClass(DebugControlsService, DebugControlsService, [PulseEnvironment])
            .useClass(RouteHelperService, RouteHelperService, [PulseEnvironment, PageInfoService])
            .useClass(UUIDService, UUIDService, [DateService, PulseEnvironment])
            .useClass(PageInfoService, PageInfoService, [PulseEnvironment])
            .useClass(CookieStorage, CookieStorage, [
                PulseEnvironment,
                CookieParser,
                CookieChangeDetector,
                SharedConfigurations,
            ])
            .useClass(WebToMobileService, WebToMobileService, [
                UnPrefixedCookieStorageToken,
                UnPrefixedStorageToken,
            ])
            .useFactory(
                UnPrefixedCookieStorageToken,
                (
                    win: PulseEnvironment,
                    cookieParser: CookieParser,
                    changeDetector: CookieChangeDetector,
                    config: SharedConfigurations,
                ) =>
                    new CookieStorage(win, cookieParser, changeDetector, {
                        ...config,
                        // don't use a key prefix to detect
                        // cookies written by other applications.
                        storagePrefix: '',
                    }),
                [PulseEnvironment, CookieParser, CookieChangeDetector, SharedConfigurations],
            )

            .useFactory(
                PrefixedCookieStorageToken,
                (
                    win: PulseEnvironment,
                    cookieParser: CookieParser,
                    changeDetector: CookieChangeDetector,
                    config: SharedConfigurations,
                ) =>
                    new CookieStorage(win, cookieParser, changeDetector, {
                        ...config,
                        // use a key prefix to detect
                        // cookies written by pulse sdk.
                        storagePrefix: config.storagePrefix,
                    }),
                [PulseEnvironment, CookieParser, CookieChangeDetector, SharedConfigurations],
            )
            .useFactory(
                DeviceIdService,
                (
                    uuid: UUIDService,
                    getStorage: StorageSelector,
                    config: SharedConfigurations,
                    cookieStorage: PulseStorage,
                    webToMobileService: WebToMobileService,
                ) =>
                    new DeviceIdService(
                        uuid,
                        getStorage(config.sessionStorageType),
                        cookieStorage,
                        config,
                        webToMobileService,
                        StorageChangeOrigin.External,
                    ),
                [
                    UUIDService,
                    StorageSelectorToken,
                    SharedConfigurations,
                    PrefixedCookieStorageToken,
                    WebToMobileService,
                ],
            )
            .useClass(DataLayerService, DataLayerService, [PulseEnvironment])
            .useFactory(
                HashStore,
                (
                    cryptoService: CryptoService,
                    dateService: DateService,
                    selectStorage: StorageSelector,
                    config: SharedConfigurations,
                ) =>
                    new HashStore(
                        selectStorage(config.hashStoreStorageType),
                        cryptoService,
                        dateService,
                    ),
                [CryptoService, DateService, StorageSelectorToken, SharedConfigurations],
            )
            .useClass(EventAdapterService, EventAdapterService, [PulseEnvironment])
            .useClass(UTMTrackerService, UTMTrackerService, [PageInfoService])
            .useFactory(
                TrafficSourceService,
                (
                    utmService: UTMTrackerService,
                    getStorage: StorageSelector,
                    config: SharedConfigurations,
                    pageInfo: PageInfoService,
                ) =>
                    new TrafficSourceService(
                        utmService,
                        getStorage(config.trafficSourceDetectorStorageType),
                        pageInfo,
                    ),
                [UTMTrackerService, StorageSelectorToken, SharedConfigurations, PageInfoService],
            )

            //
            // Storage
            ////////////////////////////
            .useClass(PersistentStorage, PersistentStorage, [
                PulseEnvironment,
                SharedConfigurations,
                Quote,
                TransientStorage,
            ])
            .useFactory(
                UnPrefixedStorageToken,
                (
                    win: PulseEnvironment,
                    config: SharedConfigurations,
                    quote: Quote,
                    transient: TransientStorage,
                ) =>
                    new PersistentStorage(
                        win,
                        {
                            ...config,
                            storagePrefix: '',
                        },
                        quote,
                        transient,
                    ),
                [PulseEnvironment, SharedConfigurations, Quote, TransientStorage],
            )
            .useClass(TabStorage, TabStorage, [
                PulseEnvironment,
                SharedConfigurations,
                Quote,
                TransientStorage,
            ])
            .useFactory(
                CookieBroadcastChannelToken,
                (
                    getStorage: StorageSelector,
                    win: PulseEnvironment,
                    config: SharedConfigurations,
                ) =>
                    new PulseBroadcastChannel(
                        'pulse-cookie-changes',
                        getStorage(config.sessionStorageType),
                        win,
                    ),
                [StorageSelectorToken, PulseEnvironment, SharedConfigurations],
            )
            .useClass(CookieChangeDetector, CookieChangeDetector, [
                CookieBroadcastChannelToken,
                PulseEnvironment,
            ])
            .useClass(CookieStorage, CookieStorage, [
                PulseEnvironment,
                CookieParser,
                CookieChangeDetector,
                SharedConfigurations,
            ])
            .useClass(Quote, Quote, [PulseEnvironment])
            .useClass(IndexedDBService, IndexedDBService, [PulseEnvironment])
            .useFactory(
                StorageSelectorToken,
                (
                    transient: PulseStorage,
                    tab: PulseStorage,
                    persistent: PulseStorage,
                    env: PulseEnvironment,
                ) =>
                    (type: StorageType): PulseStorage => {
                        if (!isLocalStorageSupported(env.win)) {
                            return transient;
                        }
                        switch (type) {
                            case 'memory':
                                return transient;
                            case 'persistent':
                                return persistent;
                            case 'tab':
                                return tab;
                        }
                    },
                [TransientStorage, TabStorage, PersistentStorage, PulseEnvironment],
            )
            .useClass(DocumentVisibilityTracker, DocumentVisibilityTracker, [PulseEnvironment])
            .useClass(ClientAttributesService, ClientAttributesService, [])
            //
            // Tabs
            ////////////////////////////
            .useFactory(
                TabManagerService,
                async (
                    selectStorage: StorageSelector,
                    transientStorage: TransientStorage,
                    lock: LockManager,
                    uuid: UUIDService,
                    quote: Quote,
                    env: PulseEnvironment,
                    config: SharedConfigurations,
                ) => {
                    const tabManager = new TabManagerService(
                        selectStorage(config.tabManagerStorageType),
                        transientStorage,
                        lock,
                        uuid,
                        quote,
                        env,
                        config,
                    );
                    await tabManager.init();

                    return tabManager;
                },
                [
                    StorageSelectorToken,
                    TransientStorage,
                    LockManagerToken,
                    UUIDService,
                    Quote,
                    PulseEnvironment,
                    SharedConfigurations,
                ],
            )
            .useFactory(
                StateService,
                (
                    getStorage: StorageSelector,
                    config: SharedConfigurations,
                    webToMobileService: WebToMobileService,
                    trafficSourceService: TrafficSourceService,
                    tabManagerService: TabManagerService,
                ) =>
                    new StateService(
                        getStorage(config.sessionStorageType),
                        config.stateStorageKey,
                        webToMobileService,
                        trafficSourceService,
                        tabManagerService,
                        StorageChangeOrigin.All,
                    ),
                [
                    StorageSelectorToken,
                    SharedConfigurations,
                    WebToMobileService,
                    TrafficSourceService,
                    TabManagerService,
                ],
            )

            //
            // Utils
            ////////////////////////////
            .useClass(ActivityTrackerUtils, ActivityTrackerUtils, [])
    );
}
